/**
 * Parts Master filter row.
 *
 * Extracted from `PartsMaster.tsx` so the page component stays under the
 * 500-line cap. Pure presentation: search, status, commodity type, and
 * (PNA-only) default warehouse.
 */
import { Search } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface CommodityType {
  id: number;
  name: string;
}

interface PartsMasterFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusChange: (value: string) => void;
  commodityGroupFilter: string;
  onCommodityChange: (value: string) => void;
  commodityOptions: CommodityType[];
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  /** Show the "Default Warehouse" Select. PNA-only, also requires data. */
  showWarehouseFilter: boolean;
  warehouseFilter: string;
  onWarehouseChange: (value: string) => void;
  warehouseOptions: string[];
  /** Right-side toolbar slot — renders the Columns + Export buttons. */
  toolbarSlot?: React.ReactNode;
}

const ALL_VALUE = 'all';

/**
 * Render the Parts Master filter / search / pagination-size row.
 *
 * @param toolbarSlot - Right-aligned controls (Columns toggle, Export button).
 */
export default function PartsMasterFilters({
  search,
  onSearchChange,
  statusFilter,
  onStatusChange,
  commodityGroupFilter,
  onCommodityChange,
  commodityOptions,
  pageSize,
  onPageSizeChange,
  showWarehouseFilter,
  warehouseFilter,
  onWarehouseChange,
  warehouseOptions,
  toolbarSlot,
}: PartsMasterFiltersProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by part number, description..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={statusFilter} onValueChange={onStatusChange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Select value={commodityGroupFilter} onValueChange={onCommodityChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Commodity Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {commodityOptions.map((cg) => (
                <SelectItem key={cg.id} value={String(cg.id)}>
                  {cg.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {showWarehouseFilter && (
            <Select value={warehouseFilter} onValueChange={onWarehouseChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Default Warehouse" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>All Warehouses</SelectItem>
                {warehouseOptions.map((wh) => (
                  <SelectItem key={wh} value={wh}>
                    {wh}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
              <SelectItem value="500">500</SelectItem>
            </SelectContent>
          </Select>

          {toolbarSlot && (
            <div className="flex items-center gap-2 ml-auto">
              {toolbarSlot}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
